import { USDV_MINT } from "@shared/tokens/usdv";
import type {
    YaasHoldingObservation,
    YaasLpHolding,
    YaasLpHoldingsSnapshot,
    YaasLpPositionIdentity
} from "@solomon-apps/shared/src/types/yaas";
import { PublicKey } from "@solomon-labs/publickey";

import { isRecord } from "./verificationResponse";

function isAddress(value: unknown): value is string {
    return typeof value === "string" && PublicKey.isBase58PublicKey(value);
}

/** Shared token/LP evidence rules at the wallet JSON boundary. */
export function isHoldingObservation(value: unknown): value is YaasHoldingObservation {
    if (!isRecord(value)) return false;
    const row = value;
    return (
        typeof row.sourceComplete === "boolean" &&
        (row.sourceComplete
            ? typeof row.amount === "string" && /^\d+(\.\d{1,6})?$/.test(row.amount)
            : row.amount === null) &&
        (row.solanaSlot === null || (typeof row.solanaSlot === "string" && /^\d+$/.test(row.solanaSlot)))
    );
}

export function isLpIdentity(value: unknown): value is YaasLpPositionIdentity {
    if (!isRecord(value)) return false;
    const row = value;
    return (
        // yaas-api passes Jachin's AMM protocol through unfiltered; any slug is a valid identity.
        typeof row.protocol === "string" &&
        row.protocol !== "" &&
        isAddress(row.poolAddress) &&
        isAddress(row.positionAddress) &&
        row.observedAssetMint === USDV_MINT &&
        (row.pairLabel === undefined || typeof row.pairLabel === "string")
    );
}

export function isLpHolding(value: unknown): value is YaasLpHolding {
    return isLpIdentity(value) && isHoldingObservation(value);
}

export function parseLpSnapshot(value: unknown, walletAddress: string): YaasLpHoldingsSnapshot {
    if (!isRecord(value)) throw new Error("Invalid LP discovery response");
    const body = value;
    if (
        body.walletAddress !== walletAddress ||
        typeof body.configVersion !== "string" ||
        !body.configVersion ||
        typeof body.complete !== "boolean" ||
        !Array.isArray(body.poolAddresses) ||
        !body.poolAddresses.every(isAddress) ||
        !Array.isArray(body.holdings) ||
        !body.holdings.every(isLpHolding) ||
        (body.complete
            ? body.incompleteReason !== null || body.holdings.some((row) => !row.sourceComplete)
            : body.incompleteReason !== "owner_enumeration_unavailable") ||
        typeof body.fetchedAt !== "string" ||
        !Number.isFinite(Date.parse(body.fetchedAt)) ||
        new Set(body.holdings.map(holdingKey)).size !== body.holdings.length
    ) {
        throw new Error("Invalid LP discovery response");
    }
    const allowedPools = new Set(body.poolAddresses);
    if (body.holdings.some((row) => !allowedPools.has(row.poolAddress)))
        throw new Error("Invalid LP discovery response");
    return {
        walletAddress,
        configVersion: body.configVersion,
        poolAddresses: body.poolAddresses,
        holdings: body.holdings,
        complete: body.complete,
        incompleteReason: body.complete ? null : "owner_enumeration_unavailable",
        fetchedAt: body.fetchedAt
    };
}

export const holdingKey = (holding: YaasLpHolding): string => `${holding.poolAddress}:${holding.positionAddress}`;

/** Partial scans cannot erase known principal or refresh it with older slot evidence. */
export function mergeLpSnapshots<T extends YaasLpHolding>(
    previous: YaasLpHoldingsSnapshot<T> | undefined,
    next: YaasLpHoldingsSnapshot<T>
): YaasLpHoldingsSnapshot<T> {
    if (!previous || previous.walletAddress !== next.walletAddress || previous.configVersion !== next.configVersion)
        return next;
    const allowed = new Set(next.poolAddresses);
    const previousRows = new Map(previous.holdings.map((row) => [holdingKey(row), row]));
    const rows = new Map(
        (next.complete ? [] : previous.holdings.filter((row) => allowed.has(row.poolAddress))).map((row) => [
            holdingKey(row),
            row
        ])
    );
    for (const row of next.holdings) {
        const old = previousRows.get(holdingKey(row));
        const regressed =
            old?.solanaSlot !== null &&
            old?.solanaSlot !== undefined &&
            (row.solanaSlot === null || BigInt(row.solanaSlot) < BigInt(old.solanaSlot));
        rows.set(holdingKey(row), old && old.amount !== null && (!row.sourceComplete || regressed) ? old : row);
    }
    return { ...next, holdings: [...rows.values()] };
}
